import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { connect } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import _ from 'lodash';
import queryString from 'query-string';
import { useMutation, useQuery } from '@apollo/client';
import { CenteredLoader, captureException } from '@playbookux/playbook-web-core';
import { DemographicsSurveyForm } from '../../components/demographics-survey-form';
import { patchProfile, getCurrentProfile } from '../../../core/actions';

import { Main, Title, TitleContainer } from './styles';
import { CREATE_GUEST_TESTER_PROFILE, TEST_SESSION } from './graphql';
import TestTypes from '../../../core/constants/testTypes';

export const DemographicsSurvey = ({ history }) => {
  const location = _.get(history, 'location', {});
  const search = _.get(location, 'search', {});
  const parsed = queryString.parse(search);
  // get test session id from url
  const testSessionId = _.get(parsed, 'session-id', null);
  const testType = _.get(parsed, 'testType', null);

  const [
    createGuestTesterProfileMutation,
    { loading, error },
  ] = useMutation(CREATE_GUEST_TESTER_PROFILE);

  const {
    loading: audienceLoading,
    data = {},
  } = useQuery(TEST_SESSION, {
    variables: {
      id: testSessionId,
    },
  });

  const audienceAttributes = data?.testSession?.tester.segment.audienceAttributes ?? [];
  const isGuest = data?.testSession?.tester.user.guest;

  const handleSubmit = useCallback(async (values) => {
    try {
      await createGuestTesterProfileMutation({
        variables: {
          testSessionId,
          guestTesterProfile: {
            ...values,
          },
        },
      });
    } catch (e) {
      captureException('CREATE_GUEST_TESTER_PROFILE_FAILURE', e);
    } finally {
      if (testType === TestTypes.MODERATED) {
        history.push({ pathname: '/screener-session/error', state: { errors: error } });
      } else if (testType === TestTypes.UNMODERATED) {
        const {
          token,
          device,
          recorder,
          lang,
        } = parsed;
        const redirectPath = `/unmoderated-session?session-id=${testSessionId}&token=${token}`
          + `&device=${device}&recorder=${recorder}&lang=${lang}`;
        history.push(redirectPath);
      } else {
        history.push(`/welcome?session-id=${testSessionId}`);
      }
    }
  }, [createGuestTesterProfileMutation, testSessionId, history, error, testType, parsed]);

  return (
    <Main>
      <TitleContainer>
        <Title as="h2">
          Before we begin the study, please enter the following information. It should match up with your profile.
        </Title>
      </TitleContainer>
      {audienceLoading
        ? <CenteredLoader />
        : (
          <DemographicsSurveyForm
            onSubmit={handleSubmit}
            loading={loading}
            error={error}
            audienceAttributes={audienceAttributes}
            isGuest={isGuest}
          />
        )}
    </Main>
  );
};

DemographicsSurvey.propTypes = {
  history: PropTypes.shape({
    location: PropTypes.shape({
      search: PropTypes.string,
    }),
    push: PropTypes.func,
  }).isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  authentication,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  patchProfile,
  getCurrentProfile,
}, dispatch);

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(DemographicsSurvey);
